import { CSSProperties } from 'react'
import clsx from 'clsx'
import { ToastItem, ToastVariant } from './ToastContext'
import styles from './Toast.module.scss'

const VARIANT_COR: Record<ToastVariant, string> = {
  default: 'var(--bs-secondary-color)',
  success: 'var(--bs-success)',
  danger: 'var(--bs-danger)',
  warning: 'var(--bs-warning)',
  info: 'var(--bs-info)',
}

const VariantIcone = ({ variant, cor }: { variant: ToastVariant; cor: string }) => {
  if (variant === 'success') {
    return (
      <svg
        width='17'
        height='17'
        viewBox='0 0 24 24'
        fill='none'
        stroke={cor}
        strokeWidth='2'
        className='flex-shrink-0 mt-1'
      >
        <path d='M20 6L9 17l-5-5' />
      </svg>
    )
  }

  if (variant === 'danger') {
    return (
      <svg
        width='17'
        height='17'
        viewBox='0 0 24 24'
        fill='none'
        stroke={cor}
        strokeWidth='2'
        className='flex-shrink-0 mt-1'
      >
        <circle cx='12' cy='12' r='9' />
        <path d='M15 9l-6 6M9 9l6 6' />
      </svg>
    )
  }

  if (variant === 'warning') {
    return (
      <svg
        width='17'
        height='17'
        viewBox='0 0 24 24'
        fill='none'
        stroke={cor}
        strokeWidth='2'
        className='flex-shrink-0 mt-1'
      >
        <path d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' />
        <path d='M12 9v4M12 17h.01' />
      </svg>
    )
  }

  if (variant === 'info') {
    return (
      <svg
        width='17'
        height='17'
        viewBox='0 0 24 24'
        fill='none'
        stroke={cor}
        strokeWidth='1.9'
        className='flex-shrink-0 mt-1'
      >
        <circle cx='12' cy='12' r='9' />
        <path d='M12 8v5M12 16h.01' />
      </svg>
    )
  }

  return null
}

type Props = {
  toast: ToastItem
  onClose: (id: string) => void
}

export const Toast = ({ toast, onClose }: Props) => {
  const {
    id,
    content,
    title,
    description,
    variant = 'default',
    accentColor,
    onClick,
    onDismiss,
  } = toast

  const cor = accentColor ?? VARIANT_COR[variant]

  const handleClick = () => {
    if (!onClick) return
    onClick()
    onClose(id)
  }

  return (
    <div
      className={clsx(styles.toast, { [styles.clickable]: !!onClick })}
      role={onClick ? 'button' : undefined}
      onClick={handleClick}
      style={{ '--toast-accent': cor } as CSSProperties}
    >
      {content ?? (
        <>
          <VariantIcone variant={variant} cor={cor} />
          <div className={styles.body}>
            {title && <div className={`fs-7 ${styles.title}`}>{title}</div>}
            {description && <div className={`fs-8 ${styles.description}`}>{description}</div>}
          </div>
        </>
      )}

      <button
        type='button'
        className={styles.close}
        onClick={(e) => {
          e.stopPropagation()
          onDismiss?.()
          onClose(id)
        }}
      >
        <svg
          width='15'
          height='15'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
        >
          <path d='M18 6L6 18M6 6l12 12' />
        </svg>
      </button>
    </div>
  )
}
